<template>
  <div>
    <v-list
      subheader
      two-line
    >
      <div v-for="(_items, type) in itemsByType" :key="`type_${type}`">
        <v-subheader inset>{{ type }}</v-subheader>
        <v-list-item
          v-for="item in _items"
          :key="`item_${item._id}`"
          @click="open(item._id)"
        >
          <v-list-item-avatar>
            <v-icon
              class="grey lighten-1"
              dark
            >
              {{ icon(type) }}
            </v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title v-text="item.value"></v-list-item-title>
            <v-list-item-subtitle v-text="item.note"></v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn icon @click.stop="remove(item._id)" :disabled="readonly">
              <v-icon>delete</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
        <v-divider inset></v-divider>
      </div>
    </v-list>

    <v-row v-show="!!alerts.length">
      <v-col>
        <v-alert
          v-for="alert of alerts"
          :key="alert.text"
          :type="alert.type"
          dense
          text
          border="left"
        >
          <v-row align="center">
            <v-col class="grow">
              {{ alert.text }}
            </v-col>
            <v-col class="shrink">
              <v-btn
                outlined
                color="primary"
                @click="add(alert.value)"
              >
                <v-icon>
                  add
                </v-icon>
                Add
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-btn
          depressed
          tile
          class="ma-0"
          :disabled="readonly"
          @click="add"
        >
          <v-icon>
            add
          </v-icon>
          Add contact information
        </v-btn>
      </v-col>
    </v-row>

    <v-dialog
      v-model="dialog"
      width="500"
    >
      <v-card>
        <v-card-text>
          <v-row class="pt-5">
            <v-col>
              <contact-details-form v-model="form" @valid="formValid = $event"/>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="closeDialog"
            :disabled="!formValid || saving"
            :loading="saving"
          >
            Save
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="dialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm-dialog
      :value="confirmDialog"
      @input="confirmRemove"
    >
      Are you sure you want to remove this contact information? This action cannot be reversed.
    </confirm-dialog>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { cloneDeep, pick, groupBy } from 'lodash'
import { ContactDetailType } from '@mymsa/common'
import { contactDetailTypeMap } from '../utilities/index.js'
import ContactDetailsForm from './forms/ContactDetailsForm.vue'
import ConfirmDialog from './dialogs/ConfirmDialog.vue'

export default {
  name: 'RegistrationContactDetails',

  components: {
    ContactDetailsForm,
    ConfirmDialog
  },

  props: {
    registrationId: {
      type: String,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    requiredContactDetailTypes: {
      type: Array,
      default: () => ([])
    }
  },

  data: () => ({
    registration: {
      contactDetails: []
    },
    items: [],
    saving: false,
    dialog: false,
    formValid: false,
    form: {},
    editedItem: -1,
    confirmDialog: false,
    confirmValue: null
  }),

  computed: {
    itemsByType () {
      return groupBy(this.items, 'type')
    },

    alerts () {
      const alerts = []

      const contactDetailTypes = this.registration.contactDetails.map(o => o.type)
      for (const requiredContactDetailType of this.requiredContactDetailTypes) {
        if (!contactDetailTypes.includes(requiredContactDetailType)) {
          const name = contactDetailTypeMap[requiredContactDetailType] ?? requiredContactDetailType
          alerts.push({
            type: 'error',
            text: `Please add a ${name}.`,
            value: requiredContactDetailType
          })
        }
      }

      return alerts
    },

    _valid () {
      return this.alerts.length < 1
    }
  },

  apollo: {
    registration: {
      query: gql`query GetRegistration($id: MongoID!) {
        registration: registrationById(_id: $id) {
          _id
          contactDetails {
            _id
            type
            value
            note
          }
        }
      }`,
      variables () {
        return {
          id: this.registrationId
        }
      },
      result ({ data }) {
        if (!data) return
        const { registration } = data
        this.items = registration.contactDetails ?? []
        this.$emit('valid', this._valid)
      }
    }
  },

  methods: {
    icon (type) {
      switch (type) {
        case ContactDetailType.PHONE:
          return 'phone'
        case ContactDetailType.MOBILE:
          return 'smartphone'
        case ContactDetailType.FAX:
          return 'print'
        case ContactDetailType.EMAIL:
          return 'alternate_email'
        case ContactDetailType.WEBSITE:
          return 'home'
        default:
          return 'help'
      }
    },

    add (type = ContactDetailType.PHONE) {
      this.editedItem = -1
      this.form = {
        type,
        value: null,
        note: null
      }
      this.dialog = true
    },

    remove (id) {
      this.confirmValue = id
      this.confirmDialog = true
    },

    async confirmRemove (value) {
      if (value) {
        const i = this.items.findIndex(o => o._id === this.confirmValue)
        this.items.splice(i, 1)
        await this.save()
        this.$emit('info', 'Contact information removed.')
      }
      this.confirmDialog = false
      this.confirmValue = null
    },

    edit (i) {
      this.activityForm = cloneDeep(this.items[i])
      this.editedItem = i
      this.dialog = true
    },

    async closeDialog () {
      if (this.editedItem >= 0) {
        Object.assign(this.items[this.editedItem], this.form)
      } else {
        this.items.push(this.form)
      }
      await this.save()
      this.$emit('info', 'Contact details saved.')
    },

    async save () {
      this.saving = true
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $record: UpdateByIdRegistrationInput!) {
            registrationUpdateById(_id: $id, record: $record){
              record {
                _id
              }
            }
          }`,
          variables: {
            id: this.registrationId,
            record: {
              contactDetails: this.items.map(item => pick(item, ['type', 'value', 'note']))
            }
          }
        })
        this.$apollo.queries.registration.refresh()
        this.$emit('change')
      } catch (e) {
        this.$emit('error', e)
        return
      } finally {
        this.saving = false
      }
      this.dialog = false
    },

    open (id) {
      const item = this.items.find(o => o._id === id)
      let href
      switch (item.type) {
        case ContactDetailType.PHONE:
        case ContactDetailType.MOBILE:
          href = `tel:${item.value}`
          break
        case ContactDetailType.FAX:
          href = `fax:${item.value}`
          break
        case ContactDetailType.EMAIL:
          href = `mailto:${item.value}`
          break
        default:
          href = item.value
          break
      }
      if (href) window.open(href, '_blank')
    }
  }
}
</script>
